import type { TFunction } from "i18next";

import type {
  AdminOrderRow,
  MarketplaceOrder,
  OrderScriptRow,
} from "../types/marketplace.types";
import {
  describeOrderScript,
  resolveOpportunitySetName,
} from "./order-script-display";

export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export interface InProgressOrderExportRow {
  vendor: string;
  orderId: string;
  script: string;
  hook: string;
  status: string;
  sent: string;
  due: string;
  opportunitySet: string;
  persona: string;
  source: string;
}

export interface AdminOrderExportRow extends InProgressOrderExportRow {
  clientOrg: string;
  clientOrgId: string;
}

function isoDate(value: string | null | undefined): string {
  if (!value) return "";
  return value.slice(0, 10);
}

function sourceLabel(order: MarketplaceOrder, t: TFunction): string {
  return order.source === "IMPORTED"
    ? t("marketplace.orders.list.export.sourceImported", "Imported")
    : t("marketplace.orders.list.export.sourceNexus", "Nexus");
}

function scriptDueYmd(order: MarketplaceOrder, script: OrderScriptRow): string {
  return script.expectedDeliveryDate ?? order.etaDate ?? "";
}

/** Flatten visible in-progress orders to one spreadsheet row per script. */
export function buildInProgressOrderExportRows(
  orders: readonly MarketplaceOrder[],
  t: TFunction,
): InProgressOrderExportRow[] {
  const unnamed = t("marketplace.orders.detail.unnamedScript", "Unnamed script");
  return orders.flatMap((order) =>
    order.scripts.map((script) => {
      const display = describeOrderScript(script, unnamed);
      return {
        vendor: order.vendor.name,
        orderId: order.id,
        script: display.title,
        hook: display.hook ?? "",
        status: t(
          `marketplace.orders.fulfillment.${script.fulfillmentStatus}`,
          script.fulfillmentStatus,
        ),
        sent: isoDate(order.sentAt) || isoDate(order.createdAt),
        due: scriptDueYmd(order, script),
        opportunitySet: resolveOpportunitySetName(script) ?? "",
        persona: display.personaName ?? "",
        source: sourceLabel(order, t),
      };
    }),
  );
}

/** Include client identity for the cross-tenant operations workbook. */
export function buildAdminOrderExportRows(
  orders: readonly AdminOrderRow[],
  t: TFunction,
): AdminOrderExportRow[] {
  return orders.flatMap((order) =>
    buildInProgressOrderExportRows([order], t).map((row) => ({
      clientOrg: order.tenant.name,
      clientOrgId: order.tenantId,
      ...row,
    })),
  );
}

function labeledSheetRows(
  rows: readonly InProgressOrderExportRow[],
  t: TFunction,
): Record<string, string>[] {
  const h = {
    vendor: t("marketplace.orders.list.columns.vendor", "Partner"),
    orderId: t("marketplace.orders.list.export.headers.orderId", "Order ID"),
    script: t("marketplace.orders.list.export.headers.script", "Script"),
    hook: t("marketplace.orders.list.export.headers.hook", "Hook"),
    status: t("marketplace.orders.list.columns.status", "Status"),
    sent: t("marketplace.orders.list.columns.sent", "Sent"),
    due: t("marketplace.orders.list.columns.due", "Due"),
    opportunitySet: t(
      "marketplace.orders.list.panel.opportunitySet",
      "Opportunity set",
    ),
    persona: t("marketplace.orders.list.panel.persona", "Persona"),
    source: t("marketplace.orders.list.panel.source", "Source"),
  };
  return rows.map((row) => ({
    [h.vendor]: row.vendor,
    [h.orderId]: row.orderId,
    [h.script]: row.script,
    [h.hook]: row.hook,
    [h.status]: row.status,
    [h.sent]: row.sent,
    [h.due]: row.due,
    [h.opportunitySet]: row.opportunitySet,
    [h.persona]: row.persona,
    [h.source]: row.source,
  }));
}

function labeledAdminSheetRows(
  rows: readonly AdminOrderExportRow[],
  t: TFunction,
): Record<string, string>[] {
  const clientOrgHeader = t(
    "marketplace.ordersDashboard.export.headers.clientOrg",
    "Client org",
  );
  const clientOrgIdHeader = t(
    "marketplace.ordersDashboard.export.headers.clientOrgId",
    "Client org ID",
  );
  const orderRows = labeledSheetRows(rows, t);
  return rows.map((row, index) => ({
    [clientOrgHeader]: row.clientOrg,
    [clientOrgIdHeader]: row.clientOrgId,
    ...orderRows[index]!,
  }));
}

function autoWidthColumns(rows: readonly Record<string, string>[]): Array<{ wch: number }> {
  const keys = Object.keys(rows[0] ?? {});
  return keys.map((key) => ({
    wch:
      Math.max(
        key.length,
        ...rows.map((row) => String(row[key] ?? "").length).slice(0, 50),
      ) + 2,
  }));
}

export function inProgressExportFilename(now = new Date()): string {
  return `marketplace-in-progress-${now.toISOString().slice(0, 10)}.xlsx`;
}

export function adminOrdersExportFilename(now = new Date()): string {
  return `marketplace-admin-orders-${now.toISOString().slice(0, 10)}.xlsx`;
}

export async function exportInProgressOrdersXlsx(
  orders: readonly MarketplaceOrder[],
  t: TFunction,
): Promise<number> {
  const rows = buildInProgressOrderExportRows(orders, t);
  if (rows.length === 0) return 0;

  const { utils, writeFile } = await import("xlsx");
  const sheetRows = labeledSheetRows(rows, t);
  const ws = utils.json_to_sheet(sheetRows);
  ws["!cols"] = autoWidthColumns(sheetRows);

  const wb = utils.book_new();
  utils.book_append_sheet(
    wb,
    ws,
    t("marketplace.orders.list.export.worksheetName", "In progress"),
  );
  writeFile(wb, inProgressExportFilename());
  return rows.length;
}

export async function exportAdminOrdersXlsx(
  orders: readonly AdminOrderRow[],
  t: TFunction,
): Promise<number> {
  const rows = buildAdminOrderExportRows(orders, t);
  if (rows.length === 0) return 0;

  const { utils, writeFile } = await import("xlsx");
  const sheetRows = labeledAdminSheetRows(rows, t);
  const ws = utils.json_to_sheet(sheetRows);
  ws["!cols"] = autoWidthColumns(sheetRows);

  const wb = utils.book_new();
  utils.book_append_sheet(
    wb,
    ws,
    t("marketplace.ordersDashboard.export.worksheetName", "Orders"),
  );
  writeFile(wb, adminOrdersExportFilename());
  return rows.length;
}
